import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

import { updateCachedSettings } from "@/hooks/useSiteSettings";
import { toast } from "sonner";
import { Loader2, Save, Palette, Eye } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Section, Field,
  HeroImageUploader, PortfolioEditor, BrandingLivePreview,
} from "./site/_shared";
import {
  LiveLinksPanel, TemplateSection, IdentityVoiceSection, ThemeSection,
  TemplateFieldsSection,
  BrandAssetsSection, CustomDomainSection, LumetierHubFeatureSection,
  LumetierCreditSection,
  EmailSenderSection, EmailTestPanel, MigrationPanel,
} from "./site/_sections";

export default function AdminSite() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [snapshot, setSnapshot] = useState(null);
  // `dirty` is tracked explicitly — flipped to true in `set(...)`, reset to false on
  // load/save/discard. Avoids `JSON.stringify(s) !== JSON.stringify(snapshot)` on
  // every render, which on a populated site settings doc was ~18 ms per keystroke
  // (the doc has portfolio arrays, theme tokens, custom-domain state, etc.).
  const [dirty, setDirty] = useState(false);

  // Mobile-only Live Preview sheet. The inline `<aside>` preview stacks
  // below a tall form on phones — auditing a colour change means
  // scrolling through 20+ fields to find it. The "Preview" pill beside
  // the mobile save bar opens a full-bleed sheet with the SAME preview
  // markup so studios can audition the change without losing their
  // place in the form.
  const [mobilePreviewOpen, setMobilePreviewOpen] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/site");
      setS(data);
      setSnapshot(data);
      setDirty(false);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); }, []);

  // Stable `set` reference — `setS` from useState is already stable, and the
  // functional updater closes over no other deps. Wrapping in useCallback with
  // an empty dep array means every child that consumes `set` keeps a stable
  // prop reference for the lifetime of the page, so React.memo on PortfolioEditor
  // / PortfolioTile actually skips renders when an unrelated field is edited.
  // The functional updater also marks the form dirty in the same render cycle.
  const set = useCallback((key, value) => {
    setS((p) => ({ ...p, [key]: value }));
    setDirty(true);
  }, []);

  // Stable setter for portfolio_images (used by PortfolioEditor).
  const setPortfolioImages = useCallback((imgs) => set("portfolio_images", imgs), [set]);
  const setPortfolioFocalPoints = useCallback((focals) => set("portfolio_focal_points", focals), [set]);

  function discard() {
    if (!snapshot) return;
    setS(snapshot);
    setDirty(false);
    toast.success("Changes discarded");
  }

  async function save() {
    setSaving(true);
    try {
      const payload = { ...s };
      delete payload.id;
      delete payload.updated_at;
      const { data } = await api.patch("/admin/site", payload);
      setS(data);
      setSnapshot(data);
      setDirty(false);
      updateCachedSettings(data);
      // Tell AdminLayout to re-apply CSS variables in case the theme changed.
      window.dispatchEvent(new CustomEvent("lumetier:theme-changed"));
      toast.success("Site settings saved");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  }

  const [tab, setTab] = useState("branding");
  if (loading || !s) {
    return <div className="text-muted-foreground">Loading site settings…</div>;
  }

  const TABS = [
    { id: "branding",  label: "Branding" },
    { id: "homepage",  label: "Homepage" },
    { id: "portfolio", label: "Portfolio" },
    { id: "booking",   label: "Booking" },
    { id: "tools",     label: "Tools" },
  ];

  return (
    <div
      className="space-y-12 pb-[calc(7rem+env(safe-area-inset-bottom,0px))] lg:pb-24"
      data-testid="admin-site-page"
    >
      {/* pb-* lift ensures the LAST form field on each tab isn't trapped
          beneath the stacked sticky save bar + mobile tab bar (~112px
          combined on mobile, ~64px on desktop where the tab bar is
          hidden). Calculated as: save-bar (~56px) + tab-bar (~56px) +
          safe-area inset for notch devices. */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 border-b border-border pb-6 sm:pb-8">
        <div className="max-w-2xl min-w-0">
          <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground mb-3 inline-flex items-center gap-1.5">
            <Palette className="w-3 h-3" /> Studio · Public site
          </div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[0.95] tracking-tight break-words">
            Branding.
          </h1>
          <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
            Everything visitors see at <span className="text-foreground font-medium break-all">/s/{s.slug || s.studio_id || "your-studio"}</span> — the hero, the about, the portfolio, the booking flow. Changes save instantly.
          </p>
        </div>
      </div>

      {/* Horizontal tab strip — Pixieset-style underline tabs */}
      <div className="flex items-center gap-7 border-b border-border overflow-x-auto -mb-px" role="tablist" data-testid="site-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            onClick={() => setTab(t.id)}
            className={`relative pb-3 text-[11px] uppercase tracking-[0.28em] transition-colors whitespace-nowrap ${
              tab === t.id ? "text-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`site-tab-${t.id}`}
          >
            {t.label}
            {tab === t.id && (
              <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-[#C9A961]" />
            )}
          </button>
        ))}
      </div>

      {tab === "branding" && (() => {
        // Build the live-preview props once so the inline desktop
        // `<aside>` and the mobile `<Sheet>` both feed the SAME
        // BrandingLivePreview from the SAME source object — no drift,
        // no duplication.
        const previewProps = {
          slug: s.slug || s.studio_id,
          studio_name: s.studio_name,
          tagline: s.tagline,
          wordmark: s.wordmark,
          hero_eyebrow: s.hero_eyebrow,
          hero_headline: s.hero_headline,
          hero_title_line1: s.hero_title_line1,
          hero_title_line2: s.hero_title_line2,
          hero_description: s.hero_description,
          hero_image: s.hero_image,
          about_eyebrow: s.about_eyebrow,
          about_title: s.about_title,
          about_body: s.about_body,
          portfolio_eyebrow: s.portfolio_eyebrow,
          portfolio_title: s.portfolio_title,
          portfolio_images: s.portfolio_images,
          portfolio_focal_points: s.portfolio_focal_points,
          logo_url: s.logo_url,
          typeface_pair: s.typeface_pair,
          theme_id: s.theme_id,
          theme_overrides: s.theme_overrides,
          template_id: s.template_id,
        };
        return (
        <div className="grid lg:grid-cols-[1fr_320px] gap-10 pb-24">
          <div className="space-y-12 min-w-0">
            {/* LIVE LINKS — the actual public URLs the studio shares with
                clients. Surfaces here so the tenant always knows where their
                site lives and can copy the booking link in two clicks. */}
            <LiveLinksPanel s={s} />

            {/* SITE TEMPLATE — premium card grid. Comes first because it's the
                structural decision the rest of the brand orbits around. */}
            <TemplateSection s={s} set={set} />

            {/* IDENTITY & VOICE — studio name, tagline, wordmark, accent, typeface */}
            <IdentityVoiceSection s={s} set={set} />

            {/* TEMPLATE-SPECIFIC STAMPS — only renders when the picked
                template ships customizable in-template chrome (Vista's
                Issue No, Heirloom's Volume, Brutalist's FRAME metadata,
                Spotlight's Feature No). Editorial / Bold / Minimal /
                Journal / Atelier / Botanical have no stamps and the
                section gracefully hides itself. */}
            <TemplateFieldsSection s={s} set={set} />

            {/* THEME */}
            <ThemeSection s={s} set={set} />

            {/* BRAND ASSETS — Logo, Cover Logo, Favicon */}
            <BrandAssetsSection s={s} set={set} />

            {/* CUSTOM DOMAIN */}
            <CustomDomainSection s={s} setS={setS} />

            {/* LUMETIER HUB FEATURE — opt-in toggle to appear on
                lumetiercreatives.com/Studios on Lumetier showcase */}
            <LumetierHubFeatureSection s={s} set={set} />

            {/* LUMETIER CREDIT — hide the "Site by Lumetier" footer mention
                (free during beta, gated to Pro plan after Stripe is wired) */}
            <LumetierCreditSection s={s} set={set} />
          </div>

          {/* Live brand preview — tailor's-room card. Sticky on desktop
              (≥lg), stacks naturally below the form on mobile/tablet so
              tenants editing from their phone can still audit Portfolio /
              Services / Contact via the surface tabs (the in-preview
              Desktop ↔ Mobile toggle handles phone-width auditioning).
              Memoized: only re-renders when the primitive props it
              consumes change, so typing in unrelated fields
              (booking_policy, contact_email, etc.) doesn't blow away
              the preview's render tree. */}
          <aside data-testid="branding-live-preview-aside" className="hidden lg:block">
            <div className="lg:sticky lg:top-24">
              <BrandingLivePreview {...previewProps} />
            </div>
          </aside>

          {/* Mobile-only Live Preview sheet. Opens from a bottom-anchored
              "Preview" pill inside the sticky save bar (see below). The
              same `previewProps` snapshot feeds the desktop aside and
              this sheet, so what you see auditioning on a phone is what
              you'd see on a 27" screen. */}
          <Sheet open={mobilePreviewOpen} onOpenChange={setMobilePreviewOpen}>
            <SheetContent
              side="bottom"
              className="lg:hidden h-[92vh] w-screen max-w-none border-0 p-0 overflow-y-auto bg-background"
              data-testid="mobile-live-preview-sheet"
            >
              <SheetHeader className="px-6 pt-6 pb-3 border-b border-border sticky top-0 bg-background/95 backdrop-blur-md z-10">
                <SheetTitle className="text-[11px] uppercase tracking-[0.32em] font-medium text-foreground">
                  Live preview · /s/{s.slug || "your-studio"}
                </SheetTitle>
                <div className="text-[10px] text-muted-foreground/70 font-serif italic">
                  Tap the close icon (top-right) to return to the form.
                </div>
              </SheetHeader>
              <div className="px-6 pb-12 pt-6">
                <BrandingLivePreview {...previewProps} />
              </div>
            </SheetContent>
          </Sheet>
        </div>
        );
      })()}

      {/* ── Sticky save bar — global (renders on all editable tabs) ──────
          Reserves a clear right-edge corner via `lg:pr-32` so the floating
          Copilot pill (anchored bottom-right) doesn't overlap the Save button.
          z-index stays BELOW the pill so the pill remains fully visible —
          the pill only floats over the save bar's empty padding area.

          On mobile the AdminLayout's `MobileTabBar` (HOME / BOOKINGS /
          GALLERIES / CLIENTS / AI) is also pinned to `bottom-0`, so we
          offset the save bar up by the tab bar's height (`h-14` = 3.5rem)
          plus `env(safe-area-inset-bottom)` for iOS notch devices, then
          collapse the offset back to 0 at `lg+` where the tab bar is
          hidden. Without this lift, the tab bar visually covers the
          save button on every phone-width screen — the bug studios kept
          screenshotting. */}
      {tab !== "tools" && (
        <div className="fixed inset-x-0 lg:left-60 z-30 border-t border-[hsl(var(--gold))]/40 bg-background/95 backdrop-blur-md px-6 lg:px-12 lg:pr-32 py-3 flex items-center justify-between gap-4 bottom-[calc(3.5rem+env(safe-area-inset-bottom,0px))] lg:bottom-0" data-testid="site-sticky-save">
          <div className="flex items-center gap-3 min-w-0">
            {dirty ? (
              <>
                <span className="relative inline-flex h-2 w-2 shrink-0">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[color:var(--gold-base)] opacity-60 animate-ping" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[color:var(--gold-base)]" />
                </span>
                <span className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--gold-base)] truncate" data-testid="site-dirty-label">
                  Unsaved changes
                </span>
              </>
            ) : (
              <span className="text-[11px] font-serif italic text-muted-foreground truncate">
                Tailor's room — every change saves to <span className="text-foreground">/s/{s.slug || "your-studio"}</span>
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {/* PREVIEW pill — mobile-only, only on Branding tab. Opens
                the full-screen Live Preview sheet so studios can audit
                their changes without scrolling past the entire form
                to find the inline preview. Sits beside Save/Discard so
                it shares the visual real estate (and the same sticky
                footprint already cleared above the mobile tab bar). */}
            {tab === "branding" && (
              <button
                type="button"
                onClick={() => setMobilePreviewOpen(true)}
                className="lg:hidden inline-flex items-center gap-1.5 h-10 px-3 text-[10px] uppercase tracking-[0.28em] border border-border hover:border-foreground/60 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Open live preview"
                data-testid="site-mobile-preview-btn"
              >
                <Eye className="w-3.5 h-3.5" />
                Preview
              </button>
            )}
            {dirty && (
              <button
                onClick={discard}
                className="h-10 px-4 text-[10px] uppercase tracking-[0.28em] text-muted-foreground hover:text-foreground border border-border hover:border-foreground/60 transition-colors"
                data-testid="site-discard-btn"
              >
                Discard
              </button>
            )}
            <button
              onClick={save}
              disabled={saving || !dirty}
              className="inline-flex items-center gap-2 px-6 h-10 bg-[#C9A961] text-stone-900 text-[11px] uppercase tracking-[0.28em] font-medium hover:opacity-90 transition-opacity disabled:opacity-40"
              data-testid="site-save-btn-sticky"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {saving ? "Saving…" : (dirty ? `Save ${tab}` : "Saved")}
            </button>
          </div>
        </div>
      )}

      {tab === "homepage" && (
        <>
          {/* HERO */}
      <Section title="Homepage hero" subtitle="The first thing visitors see when they land on your booking site.">
        <div className="grid md:grid-cols-2 gap-8">
          <HeroImageUploader
            value={s.hero_image}
            onChange={(v) => set("hero_image", v)}
            studioName={s.studio_name}
            focalX={s.hero_focal_x ?? 50}
            focalY={s.hero_focal_y ?? 50}
            onFocalChange={(x, y) => { set("hero_focal_x", x); set("hero_focal_y", y); }}
          />
          <div className="space-y-4">
            <Field label="Eyebrow (small text above headline)">
              <input value={s.hero_eyebrow || ""} onChange={(e) => set("hero_eyebrow", e.target.value)} className="input" data-testid="hero-eyebrow" />
            </Field>
            <Field label="Headline — line 1">
              <input value={s.hero_title_line1 || ""} onChange={(e) => set("hero_title_line1", e.target.value)} className="input" data-testid="hero-title-1" />
            </Field>
            <Field label="Headline — line 2">
              <input value={s.hero_title_line2 || ""} onChange={(e) => set("hero_title_line2", e.target.value)} className="input" data-testid="hero-title-2" />
            </Field>
            <Field label="Description">
              <textarea rows={3} value={s.hero_description || ""} onChange={(e) => set("hero_description", e.target.value)} className="input resize-none" data-testid="hero-desc" />
            </Field>
          </div>
        </div>

        {/* Secondary hero — only relevant for templates with an album-spread
            (two side-by-side portraits). Hidden for single-hero templates so
            the admin form stays calm. Currently: Heirloom only. */}
        {s.template_id === "heirloom" && (
          <div className="mt-8 pt-8 border-t border-border" data-testid="hero-secondary-block">
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#C9A961] mb-2">
                Heirloom · Album spread
              </div>
              <h3 className="font-serif text-lg">Second hero image</h3>
              <p className="text-xs text-muted-foreground mt-1 max-w-2xl leading-relaxed">
                The Heirloom template lays out your hero as a two-portrait
                album spread. The first hero above is the LEFT portrait; this
                is the RIGHT portrait. Leave blank to use the second image
                from your portfolio automatically.
              </p>
            </div>
            <div className="md:max-w-md">
              <HeroImageUploader
                value={s.hero_image_secondary}
                onChange={(v) => set("hero_image_secondary", v)}
                studioName={s.studio_name}
                focalX={50}
                focalY={50}
                onFocalChange={() => {}}
              />
            </div>
          </div>
        )}
      </Section>

      {/* ABOUT */}
      <Section title="About / studio intro" subtitle="The section below the hero.">
        <div className="grid md:grid-cols-2 gap-6">
          <Field label="Eyebrow">
            <input value={s.about_eyebrow || ""} onChange={(e) => set("about_eyebrow", e.target.value)} className="input" data-testid="about-eyebrow" />
          </Field>
          <Field label="Title">
            <input value={s.about_title || ""} onChange={(e) => set("about_title", e.target.value)} className="input" data-testid="about-title" />
          </Field>
          <div className="md:col-span-2">
            <Field label="Body">
              <textarea rows={4} value={s.about_body || ""} onChange={(e) => set("about_body", e.target.value)} className="input resize-none" data-testid="about-body" />
            </Field>
          </div>
        </div>
      </Section>

      {/* PORTFOLIO */}
      </>
      )}

      {tab === "portfolio" && (
        <>
      {/* PORTFOLIO */}
      <Section title="Portfolio gallery" subtitle="Selected work shown on your public booking site.">
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <Field label="Section eyebrow (small label above the heading)">
            <input
              value={s.portfolio_eyebrow ?? ""}
              onChange={(e) => set("portfolio_eyebrow", e.target.value)}
              className="input"
              placeholder="Selected Work"
              data-testid="portfolio-eyebrow"
            />
          </Field>
          <Field label="Section heading">
            <input
              value={s.portfolio_title ?? ""}
              onChange={(e) => set("portfolio_title", e.target.value)}
              className="input"
              placeholder="Recent sessions"
              data-testid="portfolio-title"
            />
          </Field>
        </div>
        <PortfolioEditor
          images={s.portfolio_images}
          focalPoints={s.portfolio_focal_points}
          onChange={setPortfolioImages}
          onFocalChange={setPortfolioFocalPoints}
        />
      </Section>

      {/* CONTACT */}
      </>
      )}

      {tab === "booking" && (
        <>
      {/* CONTACT */}
      <Section title="Contact & social" subtitle="Shown on the Contact page and in the footer.">
        <div className="grid md:grid-cols-2 gap-6">
          <Field label="Public email">
            <input value={s.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="input" data-testid="contact-email-setting" />
          </Field>
          <Field label="Phone">
            <input value={s.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className="input" data-testid="contact-phone-setting" />
          </Field>
          <Field label="Location">
            <input value={s.contact_location || ""} onChange={(e) => set("contact_location", e.target.value)} className="input" data-testid="contact-location-setting" />
          </Field>
          <Field label="Instagram URL">
            <input value={s.instagram_url || ""} onChange={(e) => set("instagram_url", e.target.value)} placeholder="https://instagram.com/allureimage" className="input" data-testid="instagram-url" />
          </Field>
          <Field label="Facebook URL">
            <input value={s.facebook_url || ""} onChange={(e) => set("facebook_url", e.target.value)} placeholder="https://facebook.com/allureimage" className="input" data-testid="facebook-url" />
          </Field>
        </div>
      </Section>

      {/* PAYMENTS / ZELLE + SENDER */}
      <Section
        title="Payments — Zelle"
        subtitle="Shown to clients on booking confirmation emails and on the booking page. Leave blank and we'll skip Zelle instructions entirely (no fallback to anyone else's account)."
      >
        {(!s.zelle_email && !s.zelle_name) && (
          <div
            className="mb-5 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
            data-testid="zelle-empty-banner"
          >
            <div className="font-medium mb-1">Set up your Zelle to accept deposits</div>
            <p className="text-amber-800/90 leading-relaxed">
              Add your studio&rsquo;s Zelle email and recipient name below so client booking confirmations show <strong>your</strong> payment details. Until you set this, Zelle instructions are omitted from outgoing emails — your bookings still record correctly, clients just won&rsquo;t see a deposit address.
            </p>
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-6">
          <Field label="Zelle email">
            <input value={s.zelle_email || ""} onChange={(e) => set("zelle_email", e.target.value)} placeholder="payments@yourstudio.com" className="input" data-testid="zelle-email-setting" />
          </Field>
          <Field label="Zelle recipient name">
            <input value={s.zelle_name || ""} onChange={(e) => set("zelle_name", e.target.value)} placeholder="Your Studio Name" className="input" data-testid="zelle-name-setting" />
          </Field>
          <Field label="Studio name (used in email subjects & signatures)">
            <input value={s.studio_name || ""} onChange={(e) => set("studio_name", e.target.value)} placeholder="Your Studio" className="input" data-testid="studio-name-setting" />
          </Field>
        </div>
        <p className="text-xs text-muted-foreground mt-4">Changes go live immediately after you click <strong>Save changes</strong>. No redeploy needed.</p>
      </Section>

      <EmailSenderSection s={s} set={set} />

      <EmailTestPanel />

      {/* POLICY */}
      <Section title="Booking policy & studio rules" subtitle="Customers must read and accept this before booking. Plain text — use blank lines for paragraph breaks.">
        <textarea
          rows={16}
          value={s.booking_policy || ""}
          onChange={(e) => set("booking_policy", e.target.value)}
          className="input resize-y font-mono text-sm leading-relaxed"
          data-testid="booking-policy"
          placeholder="1. DEPOSIT — A non-refundable deposit is required to secure your session date..."
        />
      </Section>

      <div className="flex justify-end">
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 h-11 bg-[#C9A961] text-stone-900 text-[11px] uppercase tracking-[0.28em] font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          data-testid="site-save-btn-bottom"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
      </>
      )}

      {tab === "tools" && (
        <div className="space-y-12 pb-24">
          <MigrationPanel />

          <Section title="Backup & sync" subtitle="Each environment has its own database. Use the panel above to move services, portfolio, and site copy between preview and production.">
            <p className="text-sm text-muted-foreground">
              Tools live here — outside the editorial Branding flow — because they affect the whole studio, not the public surface. To switch site template, head to <span className="text-foreground italic font-serif">Branding</span>.
            </p>
          </Section>
        </div>
      )}

      <style>{`
        .input { margin-top: .25rem; width: 100%; background: transparent; border-bottom: 1px solid hsl(var(--border)); padding: .6rem 0; outline: none; font-size: .9rem; }
        .input:focus { border-color: hsl(var(--foreground)); }
      `}</style>
    </div>
  );
}
